import React from 'react';
import useHideMobileNav from '../utils/useHideMobileNav';

/**
 * Reusable Custom Modal Component for Marshmallow Trees
 * Provides consistent styling across all popups and dialogs
 */

const CustomModal = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'confirm', // 'confirm', 'alert', 'custom'
  onConfirm = null,
  confirmText = 'Yes',
  cancelText = 'No',
  confirmButtonStyle = 'warning', // 'warning', 'success', 'danger', 'primary'
  children = null,
  messageAlign = 'center'
}) => {
  useHideMobileNav(!!isOpen);

  if (!isOpen) return null;

  const getConfirmButtonColor = () => {
    switch (confirmButtonStyle) {
      case 'success':
        return 'rgb(123, 179, 66)'; // Green
      case 'danger':
        return 'rgb(211, 47, 47)'; // Red
      case 'primary':
        return 'rgb(200, 181, 160)'; // Marshmallow brown
      case 'warning':
      default:
        return 'rgb(255, 165, 0)'; // Orange
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    try {
      if (onConfirm) {
        onConfirm();
      }
      onClose();
    } catch (error) {
      console.error('Error in modal confirm:', error);
      onClose();
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(10, 15, 25, 0.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily: "'Montserrat', sans-serif"
      }}
      onClick={handleBackdropClick}
    >
      <div 
        style={{
          backgroundColor: 'rgb(255, 255, 255)',
          border: '2px solid rgb(212, 196, 176)',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '450px',
          width: '90%',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          maxHeight: 'calc(80vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div style={{ marginBottom: '16px', textAlign: 'center' }}>
            <h5 style={{ 
              margin: 0, 
              color: 'rgb(90, 74, 66)',
              fontSize: '1.25rem',
              fontWeight: '600'
            }}>
              {title}
            </h5>
          </div>
        )}

        {/* Content */}
        <div style={{ marginBottom: '24px', textAlign: messageAlign }}>
          {message && (
            <p style={{ 
              margin: 0, 
              color: 'rgb(90, 74, 66)',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              {message}
            </p>
          )}
        </div>

        {/* Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {type === 'confirm' && (
            <>
              <button
                onClick={onClose}
                style={{
                  backgroundColor: 'rgb(255, 255, 255)',
                  border: '1px solid rgb(212, 196, 176)',
                  color: 'rgb(90, 74, 66)',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  minWidth: '80px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = 'rgb(248, 246, 244)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'rgb(255, 255, 255)';
                }}
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  backgroundColor: getConfirmButtonColor(),
                  border: `1px solid ${getConfirmButtonColor()}`,
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  minWidth: '80px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.opacity = '0.9';
                }}
                onMouseOut={(e) => {
                  e.target.style.opacity = '1';
                }}
              >
                {confirmText}
              </button>
            </>
          )}
          
          {type === 'alert' && (
            <button
              onClick={onClose}
              style={{
                backgroundColor: getConfirmButtonColor(),
                border: `1px solid ${getConfirmButtonColor()}`,
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                minWidth: '80px'
              }}
            >
              OK
            </button>
          )}

          {type === 'custom' && children}
        </div>
      </div>
    </div>
  );
};

export default CustomModal;
